import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Select,
  Tag,
  Space,
  Descriptions,
  Row,
  Col,
  Typography,
  Divider,
  Badge,
  Empty,
  Button,
  Timeline,
  Tooltip,
} from 'antd';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import Icons from '@/components/third-party/Icons';
import { get_batch_ebook_list, get_batch_ebook } from '@/utils/api';
import { defaultColors } from '@/themes/antd-theme';
import { useTheme } from '@/App';

const { Title, Text } = Typography;
const { Option } = Select;

const BatchLogBook = () => {
  const { t } = useTranslation();
  const { isDark } = useTheme();

  // Dark mode colors
  const cardBgColor = isDark ? defaultColors.palette.gray[800] : '#ffffff';
  const pageBgColor = isDark ? defaultColors.palette.gray[900] : '#f9fafb';
  const textColor = isDark ? '#ffffff' : '#1f2937';
  const secondaryTextColor = isDark ? defaultColors.palette.gray[400] : '#6b7280';
  const borderColor = isDark ? defaultColors.palette.gray[600] : '#e5e7eb';
  const nestedBgColor = isDark ? defaultColors.palette.gray[700] : '#f9fafb';
  const highlightBgColor = isDark ? '#1e3a5f' : '#eff6ff';
  const [loading, setLoading] = useState(false);
  const [batchList, setBatchList] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [ebookData, setEbookData] = useState(null);

  useEffect(() => {
    fetchBatchList();
  }, []);

  const fetchBatchList = async () => {
    setLoading(true);
    try {
      get_batch_ebook_list((response) => {
        if (response?.data?.status === 'success') {
          setBatchList(response.data.data || []);
        } else {
          toast.error(response?.data?.message || 'Failed to fetch batch list');
          setBatchList([]);
        }
        setLoading(false);
      });
    } catch (error) {
      console.error('Error fetching batch list:', error);
      toast.error('Failed to fetch batch list');
      setBatchList([]);
      setLoading(false);
    }
  };

  const fetchEbookData = async (batchNumber) => {
    setLoading(true);
    try {
      get_batch_ebook(batchNumber, (response) => {
        if (response?.data?.status === 'success') {
          setEbookData(response.data.data || null);
        } else {
          toast.error(response?.data?.message || 'Failed to fetch batch e-book');
          setEbookData(null);
        }
        setLoading(false);
      });
    } catch (error) {
      console.error('Error fetching batch e-book:', error);
      toast.error('Failed to fetch batch e-book');
      setEbookData(null);
      setLoading(false);
    }
  };

  const handleBatchChange = (batchNumber) => {
    setSelectedBatch(batchNumber);
    if (batchNumber) {
      fetchEbookData(batchNumber);
    } else {
      setEbookData(null);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      Completed: 'success',
      'In Progress': 'processing',
      Pending: 'warning',
      Failed: 'error',
      Scheduled: 'default',
    };
    return colors[status] || 'default';
  };

  const handleExport = () => {
    toast.info('Export functionality - API integration needed');
  };

  // Process Columns (for main table)
  const processColumns = [
    {
      title: 'Process',
      dataIndex: 'process_name',
      key: 'process_name',
      width: 200,
      render: (text, record) => (
        <div>
          <Text strong className="text-blue-600">
            {text}
          </Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.process_type}
          </Text>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (status) => <Badge status={getStatusColor(status)} text={status} />,
    },
    {
      title: 'Operations',
      dataIndex: 'operations',
      key: 'operations',
      width: 100,
      render: (operations) => <Tag color="blue">{operations?.length || 0}</Tag>,
    },
    {
      title: 'Start Time',
      dataIndex: 'start_time',
      key: 'start_time',
      width: 180,
      render: (text) => (text ? dayjs(text).format('DD-MMM-YYYY HH:mm') : '-'),
    },
    {
      title: 'End Time',
      dataIndex: 'end_time',
      key: 'end_time',
      width: 180,
      render: (text) => (text ? dayjs(text).format('DD-MMM-YYYY HH:mm') : '-'),
    },
    {
      title: 'Assigned To',
      dataIndex: 'assigned_to',
      key: 'assigned_to',
      width: 150,
      render: (text) => (
        <Space>
          <Icons.User className="text-blue-500" size={16} />
          <Text>{text}</Text>
        </Space>
      ),
    },
  ];

  // Operation Columns (for nested table inside process)
  const operationColumns = [
    {
      title: 'Operation',
      dataIndex: 'operation_name',
      key: 'operation_name',
      width: 200,
      render: (text, record) => (
        <div>
          <Text strong className="text-green-600">
            {text}
          </Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.operation_number} | {record.operation_type}
          </Text>
        </div>
      ),
    },
    {
      title: 'Equipment',
      dataIndex: 'machine_name',
      key: 'machine_name',
      width: 180,
      render: (text) => text || '-',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (status) => <Badge status={getStatusColor(status)} text={status} />,
    },
    {
      title: 'Start Time',
      dataIndex: 'start_time',
      key: 'start_time',
      width: 180,
      render: (text) => (text ? dayjs(text).format('DD-MMM-YYYY HH:mm') : '-'),
    },
    {
      title: 'End Time',
      dataIndex: 'end_time',
      key: 'end_time',
      width: 180,
      render: (text) => (text ? dayjs(text).format('DD-MMM-YYYY HH:mm') : '-'),
    },
    {
      title: 'Duration (min)',
      dataIndex: 'duration',
      key: 'duration',
      width: 120,
      render: (duration) => (duration ? `${duration} min` : '-'),
    },
    {
      title: 'Performed By',
      dataIndex: 'performed_by',
      key: 'performed_by',
      width: 150,
      render: (text) => (
        <Space>
          <Icons.User className="text-blue-500" size={16} />
          <Text>{text}</Text>
        </Space>
      ),
    },
    {
      title: 'Verified By',
      dataIndex: 'verified_by',
      key: 'verified_by',
      width: 150,
      render: (text) =>
        text ? (
          <Space>
            <Icons.CheckCircle className="text-green-500" size={16} />
            <Text>{text}</Text>
          </Space>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
  ];

  // Render Process Details (Operations inside process)
  const renderProcessOperations = (process) => {
    if (!process.operations || process.operations.length === 0) {
      return (
        <div className="p-4" style={{ backgroundColor: nestedBgColor }}>
          <Empty description="No operations recorded" />
        </div>
      );
    }

    return (
      <div className="p-4" style={{ backgroundColor: nestedBgColor }}>
        <Title level={5} className="mb-3" style={{ color: textColor }}>
          Operations in {process.process_name}
        </Title>

        <Table
          columns={operationColumns}
          dataSource={process.operations}
          pagination={false}
          size="small"
          rowKey="operation_id"
          bordered
          expandable={{
            expandedRowRender: (operation) => renderOperationDetails(operation),
            rowExpandable: (record) => true,
          }}
          scroll={{ x: 1400 }}
        />
      </div>
    );
  };

  // Render Operation Details (Sub-operations, ingredients, parameters, etc.)
  const renderOperationDetails = (operation) => {
    return (
      <div className="p-4" style={{ backgroundColor: cardBgColor }}>
        {/* Sub-operations Timeline */}
        {operation.sub_operations && operation.sub_operations.length > 0 && (
          <>
            <Title level={5} className="mb-4" style={{ color: textColor }}>
              Sub-Operations Timeline
            </Title>
            <Timeline
              mode="left"
              className="mb-4"
              items={operation.sub_operations.map((subOp) => ({
                color: subOp.status === 'Completed' ? 'green' : subOp.status === 'In Progress' ? 'blue' : 'gray',
                dot:
                  subOp.status === 'Completed' ? (
                    <Icons.CheckCircle className="text-green-500" size={20} />
                  ) : subOp.status === 'In Progress' ? (
                    <Icons.Clock className="text-blue-500" size={20} />
                  ) : (
                    <Icons.Circle className="text-gray-400" size={20} />
                  ),
                children: (
                  <Card size="small" className="mb-2">
                    <Row gutter={16}>
                      <Col span={12}>
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                          <div>
                            <Tag color="cyan">Step {subOp.sub_operation_id}</Tag>
                            <Text strong className="ml-2">
                              {subOp.sub_operation_name}
                            </Text>
                          </div>
                          <div>
                            <Badge status={getStatusColor(subOp.status)} text={subOp.status} />
                          </div>
                          <div>
                            <Icons.User size={14} className="text-gray-500 mr-1" />
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                              {subOp.performed_by}
                            </Text>
                          </div>
                        </Space>
                      </Col>
                      <Col span={12}>
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                          {subOp.start_time && (
                            <div>
                              <Icons.Clock size={14} className="text-gray-500 mr-1" />
                              <Text style={{ fontSize: '12px' }}>
                                {dayjs(subOp.start_time).format('DD-MMM-YYYY HH:mm')}
                              </Text>
                            </div>
                          )}
                          {subOp.end_time && (
                            <div>
                              <Icons.CheckCircle size={14} className="text-green-500 mr-1" />
                              <Text style={{ fontSize: '12px' }}>
                                {dayjs(subOp.end_time).format('DD-MMM-YYYY HH:mm')}
                              </Text>
                            </div>
                          )}
                          {subOp.remarks && (
                            <div>
                              <Text type="secondary" style={{ fontSize: '12px' }}>
                                {subOp.remarks}
                              </Text>
                            </div>
                          )}
                        </Space>
                      </Col>
                    </Row>
                  </Card>
                ),
              }))}
            />
            <Divider />
          </>
        )}

        {/* Ingredients, Parameters, Samples, etc. */}
        <Row gutter={16}>
          {/* Ingredients */}
          {operation.ingredients && operation.ingredients.length > 0 && (
            <Col span={24}>
              <Card size="small" className="mb-4" title="Materials/Ingredients">
                <Table
                  size="small"
                  dataSource={operation.ingredients}
                  pagination={false}
                  columns={[
                    { title: 'Material Code', dataIndex: 'material_code', key: 'material_code', width: 120 },
                    { title: 'Material Name', dataIndex: 'material_name', key: 'material_name', width: 200 },
                    { title: 'Required Qty', dataIndex: 'required_qty', key: 'required_qty', width: 100, render: (val) => val?.toFixed(2) },
                    { title: 'Actual Qty', dataIndex: 'actual_qty', key: 'actual_qty', width: 100, render: (val) => val?.toFixed(2) },
                    { title: 'UOM', dataIndex: 'uom', key: 'uom', width: 80 },
                    {
                      title: 'Deviation',
                      dataIndex: 'deviation',
                      key: 'deviation',
                      width: 100,
                      render: (val) => (
                        <Tag color={val === 0 ? 'green' : val > 0 ? 'orange' : 'red'}>{val?.toFixed(2)}</Tag>
                      ),
                    },
                    {
                      title: 'Performed By',
                      dataIndex: 'performed_by',
                      key: 'performed_by',
                      width: 150,
                      render: (text) => (
                        <Space>
                          <Icons.User className="text-blue-500" size={14} />
                          <Text>{text || 'N/A'}</Text>
                        </Space>
                      ),
                    },
                  ]}
                />
              </Card>
            </Col>
          )}

          {/* Parameters */}
          {operation.parameters && operation.parameters.length > 0 && (
            <Col span={24}>
              <Card size="small" className="mb-4" title="Process Parameters">
                <Table
                  size="small"
                  dataSource={operation.parameters}
                  pagination={false}
                  columns={[
                    { title: 'Parameter', dataIndex: 'parameter_name', key: 'parameter_name', width: 200 },
                    { title: 'Recorded At', dataIndex: 'recorded_at', key: 'recorded_at', width: 140, render: (v) => v || '-' },
                    {
                      title: 'Actual Value',
                      dataIndex: 'actual_value',
                      key: 'actual_value',
                      width: 100,
                      render: (v) => (v === null || v === undefined ? '—' : v),
                    },
                    {
                      title: 'Captured',
                      key: 'capture_source',
                      width: 190,
                      render: (_, r) => {
                        const src = {
                          AUTO: { text: 'Automatic', color: 'blue' },
                          PC_CONFIRMED: { text: 'Process control, confirmed', color: 'green' },
                          PC_OVERRIDE: { text: 'Operator exception', color: 'orange' },
                          MANUAL: { text: 'Manual', color: 'default' },
                        }[r.capture_source] || { text: r.capture_source || 'Manual', color: 'default' };
                        return (
                          <Space size={4} wrap>
                            <Tooltip title={r.override_reason ? `Reason: ${r.override_reason}` : undefined}>
                              <Tag color={src.color}>{src.text}</Tag>
                            </Tooltip>
                            {r.quality === 'STALE' && <Tag color="purple">Stale value</Tag>}
                            {r.quality === 'NO_DATA' && <Tag color="red">No value</Tag>}
                          </Space>
                        );
                      },
                    },
                    { title: 'UOM', dataIndex: 'uom', key: 'uom', width: 80 },
                    {
                      title: 'Status',
                      dataIndex: 'status',
                      key: 'status',
                      width: 150,
                      render: (status) => (
                        <Badge
                          status={
                            status === 'Within Limit' ? 'success' : status === 'Out of Limit' ? 'error' : 'warning'
                          }
                          text={status}
                        />
                      ),
                    },
                    {
                      title: 'Performed By',
                      dataIndex: 'performed_by',
                      key: 'performed_by',
                      width: 150,
                      render: (text) => (
                        <Space>
                          <Icons.User className="text-blue-500" size={14} />
                          <Text>{text || 'N/A'}</Text>
                        </Space>
                      ),
                    },
                  ]}
                />
              </Card>
            </Col>
          )}

          {/* Samples */}
          {operation.samples && operation.samples.length > 0 && (
            <Col span={24}>
              <Card size="small" className="mb-4" title="Samples">
                <Table
                  size="small"
                  dataSource={operation.samples}
                  rowKey="sample_id"
                  pagination={false}
                  columns={[
                    { title: 'Sample No.', dataIndex: 'sample_number', key: 'sample_number', render: (v) => v || '-' },
                    { title: 'Sample', dataIndex: 'sample_name', key: 'sample_name', render: (v) => v || '-' },
                    {
                      title: 'Type',
                      dataIndex: 'sampling_type',
                      key: 'sampling_type',
                      width: 110,
                      render: (v) => (v === 'QC' ? <Tag color="gold">Final (QC)</Tag> : v === 'IPC' ? <Tag color="purple">IPC</Tag> : <Tag>Inline</Tag>),
                    },
                    { title: 'Taken', dataIndex: 'taken_at', key: 'taken_at', width: 140, render: (v) => v || '-' },
                    { title: 'Taken By', dataIndex: 'taken_by', key: 'taken_by', render: (v) => v || '-' },
                    {
                      title: 'Required vs Actual',
                      key: 'checks',
                      render: (_, r) =>
                        r.checks?.length > 0 ? (
                          <div>
                            {r.checks.map((c, i) => (
                              <div key={i}>
                                {c.label}: <Text type="secondary">{c.required}</Text> →{' '}
                                <Text strong type={c.ok ? 'success' : 'danger'}>{c.actual}</Text>
                                {!c.ok && c.note ? <Text type="danger"> ({c.note})</Text> : null}
                              </div>
                            ))}
                          </div>
                        ) : (
                          '-'
                        ),
                    },
                    {
                      title: 'Result',
                      key: 'result',
                      width: 170,
                      render: (_, r) =>
                        r.result === 'Pass' ? (
                          <Tag color="success">Pass</Tag>
                        ) : r.result === 'Fail' && r.exception ? (
                          <Tooltip title={`${r.exception.approved_by || ''}: ${r.exception.comment}`}>
                            <Tag color="warning">Fail – exception</Tag>
                          </Tooltip>
                        ) : r.result === 'Fail' ? (
                          <Tag color="error">Fail</Tag>
                        ) : (
                          <Tag>{r.status || 'Pending'}</Tag>
                        ),
                    },
                  ]}
                />
              </Card>
            </Col>
          )}

          {/* Products */}
          {operation.products && operation.products.length > 0 && (
            <Col span={24}>
              <Card size="small" className="mb-4" title="Products/Outputs">
                <Table
                  size="small"
                  dataSource={operation.products}
                  pagination={false}
                  columns={[
                    { title: 'Product Code', dataIndex: 'product_code', key: 'product_code' },
                    { title: 'Product Name', dataIndex: 'product_name', key: 'product_name' },
                    { title: 'Quantity', dataIndex: 'quantity', key: 'quantity', render: (val) => val?.toFixed(2) },
                    { title: 'UOM', dataIndex: 'uom', key: 'uom' },
                    { title: 'Container ID', dataIndex: 'container_id', key: 'container_id' },
                  ]}
                />
              </Card>
            </Col>
          )}

          {/* Clearance Data - Only show if there's actual clearance data */}
          {operation.clearance_data &&
           (operation.clearance_data.previous_product ||
            operation.clearance_data.previous_batch ||
            operation.clearance_data.cleaning_type) && (
            <Col span={24}>
              <Card size="small" className="mb-4 bg-yellow-50 border-yellow-200" title="Line Clearance Information">
                <Descriptions bordered size="small" column={2}>
                  <Descriptions.Item label="Previous Product">{operation.clearance_data.previous_product || 'N/A'}</Descriptions.Item>
                  <Descriptions.Item label="Previous Batch">{operation.clearance_data.previous_batch || 'N/A'}</Descriptions.Item>
                  <Descriptions.Item label="Cleaning Type">{operation.clearance_data.cleaning_type || 'N/A'}</Descriptions.Item>
                  <Descriptions.Item label="Verified By">{operation.clearance_data.verified_by || 'N/A'}</Descriptions.Item>
                </Descriptions>
              </Card>
            </Col>
          )}
        </Row>
      </div>
    );
  };

  return (
    <div className="p-1.5 min-h-screen" style={{ backgroundColor: pageBgColor }}>
      <Card
        className="border border-gray-300 shadow-md"
        bordered={false}
        style={{ backgroundColor: cardBgColor }}
        title={
          <div className="flex items-center gap-2">
            <Icons.BookOpen className="text-blue-600" size={24} />
            <Title
              level={4}
              style={{
                color: textColor,
                margin: 0,
                padding: 0,
              }}
            >
              {t('Batch LogBook')}
            </Title>
          </div>
        }
        extra={
          <Space size="middle">
            <Button
              type="default"
              icon={<Icons.Download size={16} />}
              onClick={handleExport}
              className="border-blue-500 text-blue-600"
            >
              Export
            </Button>
          </Space>
        }
      >
        {/* Batch Selection */}
        <Row gutter={16} className="mb-4">
          <Col xs={24} md={12}>
            <div className="mb-2">
              <Text strong style={{ color: textColor }}>Select Batch Number:</Text>
            </div>
            <Select
              placeholder="Select a batch to view e-book"
              value={selectedBatch}
              onChange={handleBatchChange}
              style={{ width: '100%' }}
              size="large"
              showSearch
              filterOption={(input, option) =>
                (option.label || '').toLowerCase().includes(input.toLowerCase())
              }
              loading={loading}
              optionLabelProp="label"
            >
              {batchList.map((batch) => (
                <Option
                  key={batch.batch_number}
                  value={batch.batch_number}
                  label={
                    <div style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      width: '100%'
                    }}>
                      <Text strong>{batch.batch_number}</Text>
                      <Text type="secondary" style={{ fontSize: '12px', marginLeft: '8px' }}>
                        {batch.recipe_name}
                      </Text>
                    </div>
                  }
                >
                  <div style={{
                    overflow: 'hidden',
                    width: '100%'
                  }}>
                    <div style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      <Text strong>{batch.batch_number}</Text>
                    </div>
                    <div style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {batch.recipe_name} | {batch.product_name}
                      </Text>
                    </div>
                  </div>
                </Option>
              ))}
            </Select>
          </Col>
        </Row>

        {/* Batch E-Book Content */}
        {ebookData && (
          <>
            {/* Batch Header Information */}
            <Card className="mb-4" bordered style={{ backgroundColor: highlightBgColor, borderColor: isDark ? '#1e40af' : '#bfdbfe' }}>
              <Descriptions bordered size="small" column={3}>
                <Descriptions.Item label="Batch Number" span={1}>
                  <Text strong className="text-blue-600">
                    {ebookData.batch_number}
                  </Text>
                </Descriptions.Item>
                <Descriptions.Item label="Recipe Name" span={1}>
                  {ebookData.recipe_name}
                </Descriptions.Item>
                <Descriptions.Item label="Recipe Code" span={1}>
                  {ebookData.recipe_code}
                </Descriptions.Item>
                <Descriptions.Item label="Product Name" span={1}>
                  {ebookData.product_name}
                </Descriptions.Item>
                <Descriptions.Item label="Product Code" span={1}>
                  {ebookData.product_code}
                </Descriptions.Item>
                <Descriptions.Item label="Batch Size" span={1}>
                  {ebookData.batch_size} {ebookData.batch_size_uom}
                </Descriptions.Item>
                <Descriptions.Item label="Status" span={1}>
                  <Badge status={getStatusColor(ebookData.status)} text={ebookData.status} />
                </Descriptions.Item>
                <Descriptions.Item label="Start Time" span={1}>
                  {ebookData.start_time ? dayjs(ebookData.start_time).format('DD-MMM-YYYY HH:mm') : 'Not Started'}
                </Descriptions.Item>
                <Descriptions.Item label="End Time" span={1}>
                  {ebookData.end_time ? dayjs(ebookData.end_time).format('DD-MMM-YYYY HH:mm') : 'In Progress'}
                </Descriptions.Item>
                <Descriptions.Item label="Created By" span={1}>
                  {ebookData.created_by}
                </Descriptions.Item>
                <Descriptions.Item label="Scheduled Date" span={2}>
                  {ebookData.scheduled_date ? dayjs(ebookData.scheduled_date).format('DD-MMM-YYYY') : 'N/A'}
                </Descriptions.Item>
              </Descriptions>
            </Card>

            {/* BMR Approval - Horizontal Workflow */}
            {ebookData.bmr_approvals && (
              <>
                <Divider orientation="left">
                  <Title level={5} style={{ color: textColor }}>
                    <Icons.Shield className="text-blue-600 mr-2" size={20} />
                    BMR Approval
                  </Title>
                </Divider>

                {(() => {
                  // Count non-null approvals
                  const approvals = [
                    ebookData.bmr_approvals.mbmr_creation,
                    ebookData.bmr_approvals.mbmr_approval,
                    ebookData.bmr_approvals.bmr_release_approval,
                    ebookData.bmr_approvals.bmr_release
                  ];
                  const nonNullCount = approvals.filter(a => a !== null).length;

                  // Calculate span based on count
                  const colSpan = nonNullCount === 0 ? 0 : 24 / nonNullCount;

                  // Don't show section if all are null
                  if (nonNullCount === 0) return null;

                  return (
                    <Card className="mb-4" style={{ backgroundColor: isDark ? defaultColors.palette.gray[700] : '#f0f5ff' }}>
                      <Row gutter={[16, 16]}>
                        {/* MBMR Creation */}
                        {ebookData.bmr_approvals.mbmr_creation && (
                          <Col span={colSpan}>
                      <Card
                        size="small"
                        title={<Text strong style={{ fontSize: '12px', color: textColor }}>MBMR Creation</Text>}
                        style={{ backgroundColor: cardBgColor, minHeight: '200px' }}
                      >
                        <div style={{ textAlign: 'center', padding: '10px 0' }}>
                          <div style={{ marginBottom: '10px' }}>
                            <Text strong style={{ color: textColor }}>{ebookData.bmr_approvals.mbmr_creation.user}</Text>
                          </div>
                          <div style={{ marginBottom: '15px' }}>
                            <Text style={{ fontSize: '11px', color: secondaryTextColor }}>
                              {dayjs(ebookData.bmr_approvals.mbmr_creation.timestamp).format('YYYY-MM-DD HH:mm:ss.SSSSSSZ')}
                            </Text>
                          </div>
                          <div style={{
                            border: `2px solid ${isDark ? '#7c3aed' : '#9575cd'}`,
                            borderRadius: '8px',
                            padding: '30px 20px',
                            backgroundColor: isDark ? '#4c1d95' : '#f3e5f5',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: '150px'
                          }}>
                            {ebookData.bmr_approvals.mbmr_creation.stamp_url ? (
                              <img
                                src={`/media/${ebookData.bmr_approvals.mbmr_creation.stamp_url}`}
                                alt="Signature Stamp"
                                style={{ maxWidth: '150px', maxHeight: '100px', objectFit: 'contain' }}
                                onError={(e) => {
                                  console.error('Image load error:', e.target.src);
                                  e.target.style.display = 'none';
                                }}
                              />
                            ) : (
                              <>
                                <div style={{
                                  width: '60px',
                                  height: '40px',
                                  backgroundColor: isDark ? '#a78bfa' : '#9575cd',
                                  borderRadius: '50% 50% 0 0',
                                  marginBottom: '5px'
                                }}></div>
                                <div style={{
                                  width: '80px',
                                  height: '30px',
                                  backgroundColor: isDark ? '#a78bfa' : '#9575cd',
                                  borderRadius: '12px'
                                }}></div>
                              </>
                            )}
                            <div style={{ marginTop: '10px', fontSize: '10px' }}>
                              <Text style={{ color: secondaryTextColor }}>Digitally signed by</Text>
                            </div>
                            <Text strong style={{ fontSize: '11px', color: textColor }}>{ebookData.bmr_approvals.mbmr_creation.user}</Text>
                          </div>
                        </div>
                      </Card>
                          </Col>
                        )}

                        {/* MBMR Approval */}
                        {ebookData.bmr_approvals.mbmr_approval && (
                          <Col span={colSpan}>
                      <Card
                        size="small"
                        title={<Text strong style={{ fontSize: '12px', color: textColor }}>MBMR Approval</Text>}
                        style={{ backgroundColor: cardBgColor, minHeight: '200px' }}
                      >
                        <div style={{ textAlign: 'center', padding: '10px 0' }}>
                          <div style={{ marginBottom: '10px' }}>
                            <Text strong style={{ color: textColor }}>{ebookData.bmr_approvals.mbmr_approval.user}</Text>
                          </div>
                          <div style={{ marginBottom: '15px' }}>
                            <Text style={{ fontSize: '11px', color: secondaryTextColor }}>
                              {dayjs(ebookData.bmr_approvals.mbmr_approval.timestamp).format('YYYY-MM-DD HH:mm:ss.SSSSSSZ')}
                            </Text>
                          </div>
                          <div style={{
                            border: `2px solid ${isDark ? '#7c3aed' : '#9575cd'}`,
                            borderRadius: '8px',
                            padding: '30px 20px',
                            backgroundColor: isDark ? '#4c1d95' : '#f3e5f5',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: '150px'
                          }}>
                            {ebookData.bmr_approvals.mbmr_approval.stamp_url ? (
                              <img
                                src={`/media/${ebookData.bmr_approvals.mbmr_approval.stamp_url}`}
                                alt="Signature Stamp"
                                style={{ maxWidth: '150px', maxHeight: '100px', objectFit: 'contain' }}
                                onError={(e) => {
                                  console.error('Image load error:', e.target.src);
                                  e.target.style.display = 'none';
                                }}
                              />
                            ) : (
                              <>
                                <div style={{
                                  width: '60px',
                                  height: '40px',
                                  backgroundColor: isDark ? '#a78bfa' : '#9575cd',
                                  borderRadius: '50% 50% 0 0',
                                  marginBottom: '5px'
                                }}></div>
                                <div style={{
                                  width: '80px',
                                  height: '30px',
                                  backgroundColor: isDark ? '#a78bfa' : '#9575cd',
                                  borderRadius: '12px'
                                }}></div>
                              </>
                            )}
                            <div style={{ marginTop: '10px', fontSize: '10px' }}>
                              <Text style={{ color: secondaryTextColor }}>Digitally signed by</Text>
                            </div>
                            <Text strong style={{ fontSize: '11px', color: textColor }}>{ebookData.bmr_approvals.mbmr_approval.user}</Text>
                          </div>
                        </div>
                      </Card>
                          </Col>
                        )}

                        {/* BMR Release Approval */}
                        {ebookData.bmr_approvals.bmr_release_approval && (
                          <Col span={colSpan}>
                      <Card
                        size="small"
                        title={<Text strong style={{ fontSize: '12px', color: textColor }}>BMR Release Approval</Text>}
                        style={{ backgroundColor: cardBgColor, minHeight: '200px' }}
                      >
                        <div style={{ textAlign: 'center', padding: '10px 0' }}>
                          <div style={{ marginBottom: '10px' }}>
                            <Text strong style={{ color: textColor }}>{ebookData.bmr_approvals.bmr_release_approval.user}</Text>
                          </div>
                          <div style={{ marginBottom: '15px' }}>
                            <Text style={{ fontSize: '11px', color: secondaryTextColor }}>
                              {dayjs(ebookData.bmr_approvals.bmr_release_approval.timestamp).format('YYYY-MM-DD HH:mm:ss.SSSSSSZ')}
                            </Text>
                          </div>
                          <div style={{
                            border: `2px solid ${isDark ? '#7c3aed' : '#9575cd'}`,
                            borderRadius: '8px',
                            padding: '30px 20px',
                            backgroundColor: isDark ? '#4c1d95' : '#f3e5f5',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: '150px'
                          }}>
                            {ebookData.bmr_approvals.bmr_release_approval.stamp_url ? (
                              <img
                                src={`/media/${ebookData.bmr_approvals.bmr_release_approval.stamp_url}`}
                                alt="Signature Stamp"
                                style={{ maxWidth: '150px', maxHeight: '100px', objectFit: 'contain' }}
                                onError={(e) => {
                                  console.error('Image load error:', e.target.src);
                                  e.target.style.display = 'none';
                                }}
                              />
                            ) : (
                              <>
                                <div style={{
                                  width: '60px',
                                  height: '40px',
                                  backgroundColor: isDark ? '#a78bfa' : '#9575cd',
                                  borderRadius: '50% 50% 0 0',
                                  marginBottom: '5px'
                                }}></div>
                                <div style={{
                                  width: '80px',
                                  height: '30px',
                                  backgroundColor: isDark ? '#a78bfa' : '#9575cd',
                                  borderRadius: '12px'
                                }}></div>
                              </>
                            )}
                            <div style={{ marginTop: '10px', fontSize: '10px' }}>
                              <Text style={{ color: secondaryTextColor }}>Digitally signed by</Text>
                            </div>
                            <Text strong style={{ fontSize: '11px', color: textColor }}>{ebookData.bmr_approvals.bmr_release_approval.user}</Text>
                          </div>
                        </div>
                      </Card>
                          </Col>
                        )}

                        {/* BMR Release */}
                        {ebookData.bmr_approvals.bmr_release && (
                          <Col span={colSpan}>
                      <Card
                        size="small"
                        title={<Text strong style={{ fontSize: '12px', color: textColor }}>BMR Release</Text>}
                        style={{ backgroundColor: cardBgColor, minHeight: '200px' }}
                      >
                        <div style={{ textAlign: 'center', padding: '10px 0' }}>
                          <div style={{ marginBottom: '10px' }}>
                            <Text strong style={{ color: textColor }}>{ebookData.bmr_approvals.bmr_release.user}</Text>
                          </div>
                          <div style={{ marginBottom: '15px' }}>
                            <Text style={{ fontSize: '11px', color: secondaryTextColor }}>
                              {dayjs(ebookData.bmr_approvals.bmr_release.timestamp).format('YYYY-MM-DD HH:mm:ss.SSSSSSZ')}
                            </Text>
                          </div>
                          <div style={{
                            border: `2px solid ${isDark ? '#7c3aed' : '#9575cd'}`,
                            borderRadius: '8px',
                            padding: '30px 20px',
                            backgroundColor: isDark ? '#4c1d95' : '#f3e5f5',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: '150px'
                          }}>
                            {ebookData.bmr_approvals.bmr_release.stamp_url ? (
                              <img
                                src={`/media/${ebookData.bmr_approvals.bmr_release.stamp_url}`}
                                alt="Signature Stamp"
                                style={{ maxWidth: '150px', maxHeight: '100px', objectFit: 'contain' }}
                                onError={(e) => {
                                  console.error('Image load error:', e.target.src);
                                  e.target.style.display = 'none';
                                }}
                              />
                            ) : (
                              <>
                                <div style={{
                                  width: '60px',
                                  height: '40px',
                                  backgroundColor: isDark ? '#a78bfa' : '#9575cd',
                                  borderRadius: '50% 50% 0 0',
                                  marginBottom: '5px'
                                }}></div>
                                <div style={{
                                  width: '80px',
                                  height: '30px',
                                  backgroundColor: isDark ? '#a78bfa' : '#9575cd',
                                  borderRadius: '12px'
                                }}></div>
                              </>
                            )}
                            <div style={{ marginTop: '10px', fontSize: '10px' }}>
                              <Text style={{ color: secondaryTextColor }}>Digitally signed by</Text>
                            </div>
                            <Text strong style={{ fontSize: '11px', color: textColor }}>{ebookData.bmr_approvals.bmr_release.user}</Text>
                          </div>
                        </div>
                      </Card>
                          </Col>
                        )}
                      </Row>
                    </Card>
                  );
                })()}
              </>
            )}

            <Divider orientation="left">
              <Title level={5} style={{ color: textColor }}>Batch Processes ({ebookData.processes?.length || 0})</Title>
            </Divider>

            {/* Processes Table */}
            <Table
              columns={processColumns}
              dataSource={ebookData.processes}
              loading={loading}
              rowKey="process_id"
              pagination={false}
              bordered
              expandable={{
                expandedRowRender: renderProcessOperations,
                rowExpandable: (record) => record.operations && record.operations.length > 0,
              }}
              scroll={{ x: 1200 }}
            />
          </>
        )}

        {/* No Selection */}
        {!selectedBatch && (
          <div className="text-center py-12">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <span>
                  <Text className="text-lg" style={{ color: secondaryTextColor }}>
                    Please select a batch number to view e-book
                  </Text>
                </span>
              }
            />
          </div>
        )}
      </Card>
    </div>
  );
};

export default BatchLogBook;
